// features/countries/page/country-upsert/country-upsert.ts
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { AppRoutes } from '@core/constants/app.routes';
import { Country, CountryCreate, CountryUpdate } from '@core/entities/models/country.model';
import { CountryService } from '@core/services/use-cases/country.service';
import { BreadcrumbService } from '@shared/services/breadcrumb.service';
import { SnackbarService } from '@shared/services/snackbar.service';

import { UiMoleculeInputComponent } from '@shared/components/molecules/ui-molecule-input/ui-molecule-input.component';
import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';

@Component({
  selector: 'app-country-upsert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    UiMoleculeInputComponent,
    UiAtomButtonComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    UiOrganismMainNavComponent,
  ],
  templateUrl: './country-upsert.html',
  styleUrl: './country-upsert.scss',
})
export class CountryUpsert {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly countryService = inject(CountryService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly snackbar = inject(SnackbarService);

  protected readonly AppRoutes = AppRoutes;

  protected readonly form: FormGroup;

  protected editingId: string | null = null;
  protected currentCountry: Country | null = null;
  protected isLoading = false;

  constructor() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
    });

    const idFromRoute = this.route.snapshot.paramMap.get('id');
    if (idFromRoute) {
      this.loadForEdit(idFromRoute);
    }
  }

  protected get nameControl() {
    return this.form.get('name');
  }

  private loadForEdit(id: string): void {
    this.countryService.getById(id).subscribe({
      next: (country) => {
        this.editingId = country.id;
        this.currentCountry = country;

        this.form.patchValue({
          name: country.name,
        });
      },
      error: (err) => {
        this.snackbar.notifyHttpError(err);
        this.backToList();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Submit / navegación
  // ---------------------------------------------------------------------------

  protected onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    if (this.editingId && this.currentCountry) {
      // Modo edición: usar CountryUpdate
      const payload: CountryUpdate = {
        name: raw.name,
      };

      this.countryService.update(this.editingId, payload).subscribe({
        next: () => {
          // Invalidar caché de breadcrumbs para este country
          this.breadcrumbService.invalidateCountryCache(this.editingId!);
          this.backToList();
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    } else {
      // Modo creación: usar CountryCreate
      const payload: CountryCreate = {
        name: raw.name,
      };

      this.isLoading = true;

      this.countryService.create(payload).subscribe({
        next: () => {
          this.isLoading = false;
          this.backToList();
        },
        error: (err) => {
          this.isLoading = false;
          this.snackbar.notifyHttpError(err);
        },
      });
    }
  }

  protected cancel(): void {
    this.backToList();
  }

  private backToList(): void {
    void this.router.navigate(['/', 'admin', AppRoutes.countries]);
  }
}
